import React from 'react';
import type { SxProps, Theme } from '@mui/material/styles';

import { Box } from 'ui/box';
import ProgressRing from 'ui/progress-ring';

import LessonCompletedIndicator from 'containers/user/learn/containers/content/cources/course-details/course-sidebar/course-sidebar-item/lesson-completed-indicator';

const LESSON_COMPLETED_PROGRESS = 100;

const LIST_ITEM_SX: SxProps<Theme> = {
  p: 1,
  borderRadius: '0.375rem',
  transitionProperty:
    'color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter',
  transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
  transitionDuration: '150ms',
};

const LINK_SX: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  bgcolor: 'transparent',
  color: 'tokens.gray.600',
  fontSize: '0.875rem',
  lineHeight: '1.25rem',
  fontWeight: 600,
  textDecoration: 'none',
  '&:hover': { color: 'tokens.navy.800' },
};

interface LessonsSidebarItemClickArgs {
  event: React.MouseEvent<HTMLAnchorElement>;
  to: string;
  id: string | number;
}

interface LessonsSidebarItemProps {
  index: number;
  id: string | number;
  name?: string;
  onClick: (args: LessonsSidebarItemClickArgs) => void;
  courseId: string | number;
  currentLessonId: string | number;
  progress?: number;
}

const LessonsSidebarItem = React.memo(
  ({
    index,
    id,
    name,
    onClick,
    courseId,
    currentLessonId,
    progress,
  }: LessonsSidebarItemProps): React.JSX.Element => {
    const isActive = currentLessonId == id; //Might be number and string

    const lessonLink = `/learn/courses/${courseId}/lesson/${id}`;
    const isCompleted = (progress ?? 0) >= LESSON_COMPLETED_PROGRESS;

    return (
      <Box
        component="li"
        sx={[LIST_ITEM_SX, isActive && { bgcolor: 'tokens.navy.50' }]}
      >
        <Box
          component="a"
          href={lessonLink}
          onClick={(e: React.MouseEvent<HTMLAnchorElement>) =>
            onClick({ event: e, to: lessonLink, id })
          }
          sx={[LINK_SX, isActive && { color: 'tokens.navy.800' }]}
        >
          <Box sx={{ maxWidth: '200px', overflowWrap: 'break-word' }}>
            <span>{`${index + 1}. `}</span>
            {name}
          </Box>

          {isCompleted ? (
            <LessonCompletedIndicator data-testid="lesson-progress-ring" />
          ) : (
            <ProgressRing value={progress} data-testid="lesson-progress-ring" />
          )}
        </Box>
      </Box>
    );
  }
);

export default LessonsSidebarItem;
